import { useState } from 'react'

import { Working } from '~/components/working'
import { reached, removed, useWork } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { FoodMark } from '~/components/food-mark'
import { Menu, MenuItem } from '~/components/editor-menu'
import { ChevronDownIcon, DeleteIcon } from '~/components/icons'
import { NutrientFields } from '~/components/nutrient-fields'
import {
  EMPTY_NUTRIENT_TEXT,
  nutrientText,
  nutrientsTyped,
  type NutrientText,
} from '~/lib/nutrient-text'
import { PageDialog } from '~/components/page-dialog'
import { useToast } from '~/components/toast-context'
import { Segmented } from '~/components/ui/segmented'
import { UnitInput } from '~/components/unit-input'
import { useApi } from '~/data/data-context'
import {
  DENSITY_MEASURES,
  densityFromWeight,
  referenceSaid,
  weighsPerMeasure,
  type DensityMeasure,
  type Food,
  type Portion,
  type Reference,
} from '~/data/nutrition'
import type { AiLabel } from '~/api/endpoints'
import { useFocusOnMount } from '~/lib/focus-on-mount'

interface PortionText {
  name: string
  amount: string
  unit: 'g' | 'ml'
}

/** The density as the form asks it: what one of a volume measure of the food weighs. */
interface DensityText {
  measure: DensityMeasure
  weight: string
}

interface FoodDraft {
  name: string
  brand: string
  barcode: string
  reference: Reference
  /** The portion the nutrients are per, where they are per one. */
  per: PortionText
  nutrients: NutrientText
  portions: PortionText[]
  density: DensityText
}

/** What a form for a new food can open holding: a name, a code, or what the AI read off a packet. */
export interface FoodSeed {
  name?: string
  barcode?: string
  label?: AiLabel
}

function draftOf(food: Food | null, seed: FoodSeed): FoodDraft {
  const [per, ...rest] = food?.reference === 'portion' ? food.portions : []
  const label = seed.label
  if (food === null && label !== undefined) {
    // What the AI read, as a draft rather than a food: looked over and changed like anything
    // typed in, and saved only by hand.
    return {
      name: label.name,
      brand: label.brand ?? '',
      barcode: label.barcode ?? '',
      reference: label.reference,
      per: { name: '', amount: '', unit: 'g' },
      nutrients: nutrientText(label.nutrients),
      density: { measure: 'cup', weight: '' },
      portions:
        label.servingAmount !== null && label.servingAmount > 0
          ? [
              {
                name: label.servingName ?? 'serving',
                amount: String(label.servingAmount),
                unit: label.reference === '100ml' ? 'ml' : 'g',
              },
            ]
          : [],
    }
  }
  return {
    name: food?.name ?? seed.name ?? '',
    brand: food?.brand ?? '',
    barcode: food?.barcode ?? seed.barcode ?? '',
    reference: food?.reference ?? '100g',
    per: {
      name: per?.name ?? '',
      amount: per === undefined ? '' : String(per.amount),
      unit: per?.unit ?? 'g',
    },
    nutrients: food === null ? EMPTY_NUTRIENT_TEXT : nutrientText(food.nutrients),
    /*
     * A food that already carries a density opens on the cup — the nutrition spec, Own foods.
     * A millilitre of it weighs about a gram whatever it is, and a figure like 1.031 says
     * nothing to anybody; what a cup of it comes to is a weight a person recognises.
     */
    density: {
      measure: 'cup',
      weight: food?.density == null ? '' : String(weighsPerMeasure(food.density, 'cup')),
    },
    portions: (food?.reference === 'portion' ? rest : (food?.portions ?? [])).map((portion) => ({
      name: portion.name,
      amount: String(portion.amount),
      unit: portion.unit,
    })),
  }
}

/**
 * The density a form's row comes to: nothing where no weight was typed, and nothing readable
 * where what was typed is not a weight, which holds the form back from saving.
 */
function densityOf(text: DensityText): number | null | undefined {
  if (text.weight.trim() === '') {
    return null
  }
  return densityFromWeight(Number(text.weight), text.measure) ?? undefined
}

function portionOf(text: PortionText): Portion | null {
  const amount = Number(text.amount)
  if (text.name.trim() === '' || !Number.isFinite(amount) || amount <= 0) {
    return null
  }
  return { name: text.name.trim(), amount, unit: text.unit }
}

/**
 * A food's form, per the spec's Own foods: a name, optionally a brand and a barcode, the
 * nutrients per a reference amount — a hundred grams, a hundred millilitres, or one portion
 * with its name and weight — and any further portions. A library food opens here to read, and
 * can be saved as a copy of one's own to change.
 */
export function FoodFormDialog({
  food = null,
  seed = {},
  onClose,
  onSaved,
  onDeleted,
}: {
  /** The food being edited, read, or copied; null for a new one. */
  food?: Food | null
  seed?: FoodSeed
  onClose: () => void
  onSaved: (saved: Food) => void
  onDeleted?: ((id: string) => void) | undefined
}) {
  const api = useApi()
  const toast = useToast()
  const library = food !== null && food.source === 'library'
  const [reading, setReading] = useState(library)
  const [draft, setDraft] = useState(() => draftOf(food, seed))
  const saving = useWork()
  const [leaving, setLeaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const nameField = useFocusOnMount<HTMLInputElement>()

  const start = draftOf(food, seed)
  /*
   * The measure a density is stated against says nothing on its own — flicking it over an
   * empty weight changes the form's reading of nothing, and is not something to lose.
   */
  const said = (of: typeof draft) =>
    JSON.stringify(of.density.weight.trim() === '' ? { ...of, density: { weight: '' } } : of)
  const dirty = !reading && said(draft) !== said(start)

  const nutrients = nutrientsTyped(draft.nutrients)
  const per = portionOf(draft.per)
  const extras = draft.portions.map(portionOf)
  const kept = extras.filter((portion): portion is Portion => portion !== null)
  const density = densityOf(draft.density)
  const complete =
    draft.name.trim() !== '' &&
    nutrients !== null &&
    (draft.reference !== 'portion' || per !== null) &&
    kept.length === extras.length &&
    density !== undefined

  const askToClose = () => {
    if (dirty) {
      setLeaving(true)
    } else {
      onClose()
    }
  }

  const save = () =>
    saving.run(async () => {
      if (!complete) {
        return
      }
      try {
        const id = food?.id != null && !library ? food.id : crypto.randomUUID()
        const saved = await api.putFood(id, {
          name: draft.name.trim(),
          emoji: null,
          brand: draft.brand.trim() === '' ? null : draft.brand.trim(),
          barcode: draft.barcode.trim() === '' ? null : draft.barcode.trim(),
          reference: draft.reference,
          nutrients,
          portions: [...(draft.reference === 'portion' && per !== null ? [per] : []), ...kept],
          // A copy of a liquid stays a liquid: the row opens on what the original weighs.
          density: density ?? null,
        })
        toast(food === null || library ? 'Food saved.' : 'Food updated.')
        onSaved(saved)
      } catch {
        toast('That did not reach us. Try again in a moment.')
      }
    })

  const remove = async () => {
    if (food?.id === null || food?.id === undefined || onDeleted === undefined) {
      return
    }
    const id = food.id
    await reached(() => removed(() => api.deleteFood(id)))
    toast(`${food.name} deleted.`)
    onDeleted(food.id)
  }

  const title =
    reading && food !== null ? food.name : food === null || library ? 'New food' : 'Edit food'
  const set = <K extends keyof FoodDraft>(key: K, value: FoodDraft[K]) => {
    setDraft((held) => ({ ...held, [key]: value }))
  }
  const box =
    'w-full rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent'

  return (
    <PageDialog title={title} onClose={askToClose} className="max-w-xl">
      {reading && food !== null ? (
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            From the library. It cannot be changed, but a copy of it can be saved as your own.
          </p>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {food.brand !== null && <Fact term="Brand" value={food.brand} />}
            <Fact term="Per" value={referenceSaid(food)} />
            {Object.entries(food.nutrients).map(([name, value]) =>
              value === null ? null : <Fact key={name} term={name} value={String(value)} />,
            )}
          </dl>
          {food.portions.length > 0 && (
            <p className="text-sm text-ink-muted">
              Portions:{' '}
              {food.portions.map((p) => `${p.name} (${String(p.amount)} ${p.unit})`).join(', ')}
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink"
              onClick={() => {
                setReading(false)
              }}
            >
              Save a copy to change
            </button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
        >
          {/* The face it will wear stands beside the name it is read off, and follows it as it
              is typed: nothing chooses a face. */}
          <div className="flex items-end gap-3">
            <FoodMark name={draft.name} size="size-12 text-2xl" />
            <label className="block min-w-0 flex-1">
              <span className="mb-1 block text-sm text-ink-muted">Name</span>
              <input
                aria-label="Name"
                ref={nameField}
                value={draft.name}
                onChange={(event) => {
                  set('name', event.target.value)
                }}
                className={box}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-sm text-ink-muted">Brand</span>
              <input
                aria-label="Brand"
                value={draft.brand}
                onChange={(event) => {
                  set('brand', event.target.value)
                }}
                className={box}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-ink-muted">Barcode</span>
              <input
                aria-label="Barcode"
                inputMode="numeric"
                value={draft.barcode}
                onChange={(event) => {
                  set('barcode', event.target.value)
                }}
                className={box}
              />
            </label>
          </div>

          <fieldset>
            <legend className="mb-1 text-sm text-ink-muted">Nutrients per</legend>
            <Segmented
              label="Nutrients per"
              options={[
                { value: '100g', label: '100 g' },
                { value: '100ml', label: '100 ml' },
                { value: 'portion', label: 'Portion' },
              ]}
              value={draft.reference}
              onValue={(next) => {
                set('reference', next)
              }}
            />
            {draft.reference === 'portion' && (
              <div className="mt-3">
                <PortionRow
                  text={draft.per}
                  onText={(next) => {
                    set('per', next)
                  }}
                />
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend className="mb-1 text-sm text-ink-muted">How much it weighs</legend>
            <DensityRow
              text={draft.density}
              onText={(next) => {
                set('density', next)
              }}
            />
            <p className="mt-2 text-sm text-ink-muted">
              Only for something pourable. Weigh a cup of it and this food can be logged by volume
              and by weight alike.
            </p>
          </fieldset>

          <NutrientFields
            text={draft.nutrients}
            onText={(next) => {
              set('nutrients', next)
            }}
          />

          <fieldset className="space-y-3">
            <legend className="text-sm text-ink-muted">More portions</legend>
            {draft.portions.map((portion, index) => (
              <PortionRow
                key={index}
                text={portion}
                onText={(next) => {
                  set(
                    'portions',
                    draft.portions.map((held, at) => (at === index ? next : held)),
                  )
                }}
                onRemove={() => {
                  set(
                    'portions',
                    draft.portions.filter((_, at) => at !== index),
                  )
                }}
              />
            ))}
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm text-accent transition-colors hover:bg-hover"
              onClick={() => {
                set('portions', [...draft.portions, { name: '', amount: '', unit: 'g' }])
              }}
            >
              Add a portion
            </button>
          </fieldset>

          <div className="flex items-center gap-3 pt-1">
            {food !== null && !library && onDeleted !== undefined && (
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-danger transition-colors hover:bg-danger/10"
                onClick={() => {
                  setDeleting(true)
                }}
              >
                <DeleteIcon aria-hidden className="size-5" />
                Delete
              </button>
            )}
            <span className="flex-1" />
            <button
              type="submit"
              aria-busy={saving.working || undefined}
              disabled={!complete}
              className="relative rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
            >
              <Working shown={saving.shown}>Save</Working>
            </button>
          </div>
        </form>
      )}

      {leaving && (
        <ConfirmDialog
          title="Discard this food?"
          body="What you have entered is not saved."
          confirm="Discard"
          onConfirm={onClose}
          onDismiss={() => {
            setLeaving(false)
          }}
        />
      )}
      {deleting && food !== null && (
        <ConfirmDialog
          title={`Delete ${food.name}?`}
          body="Entries already logged with it keep what they say. The food itself goes."
          confirm="Delete"
          onConfirm={remove}
          onDismiss={() => {
            setDeleting(false)
          }}
        />
      )}
    </PageDialog>
  )
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <div className="rounded-xl bg-surface-raised px-3 py-2">
      <dt className="text-xs text-ink-muted capitalize">{term}</dt>
      <dd>{value}</dd>
    </div>
  )
}

export function Choice({ label, on, onPick }: { label: string; on: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={`flex h-8 items-center rounded-full px-3.5 text-sm transition-colors ${
        on
          ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
          : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * The density, asked as a weight against a measure rather than as a number of grams per
 * millilitre: one cup of it weighs so much. Nobody knows their smoothie is 1.04 g/ml; anybody
 * can put a cup of it on the scales.
 */
function DensityRow({ text, onText }: { text: DensityText; onText: (next: DensityText) => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        aria-label="Weight"
        value={text.weight}
        onChange={(event) => {
          onText({ ...text, weight: event.target.value })
        }}
        className="w-28 rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
      />
      {/* The whole row reads as the figure it stands for: 247.44 g/cup is a density said out loud. */}
      <span className="text-lg text-ink-muted">g/</span>
      <Menu
        label="Measure"
        size="h-10 w-auto gap-1 px-2.5"
        icon={
          <span className="flex items-center gap-1 text-lg text-ink">
            {text.measure}
            <ChevronDownIcon aria-hidden className="size-4" />
          </span>
        }
      >
        {DENSITY_MEASURES.map((measure) => (
          <MenuItem
            key={measure}
            checked={measure === text.measure}
            onSelect={() => {
              /*
               * The figure said differently rather than a new figure: a cup weighing 247.44 g
               * is a millilitre weighing 1.031, and picking the other measure must not leave
               * the old weight standing against it as a density nobody meant.
               */
              const held = densityFromWeight(Number(text.weight), text.measure)
              onText({
                ...text,
                measure,
                weight: held === null ? text.weight : String(weighsPerMeasure(held, measure)),
              })
            }}
          >
            {measure}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}

function PortionRow({
  text,
  onText,
  onRemove,
}: {
  text: PortionText
  onText: (next: PortionText) => void
  onRemove?: (() => void) | undefined
}) {
  return (
    <div className="flex items-end gap-2">
      <label className="block flex-1">
        <span className="mb-1 block text-sm text-ink-muted">Portion name</span>
        <input
          aria-label="Portion name"
          placeholder="slice"
          value={text.name}
          onChange={(event) => {
            onText({ ...text, name: event.target.value })
          }}
          className="w-full rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 placeholder:text-ink-muted focus:ring-2 focus:ring-accent"
        />
      </label>
      <div className="w-32">
        <UnitInput
          value={text.amount}
          unit={text.unit}
          label="Weighs"
          onValue={(next) => {
            onText({ ...text, amount: next })
          }}
        />
      </div>
      <button
        type="button"
        aria-pressed={text.unit === 'ml'}
        aria-label="Measured in millilitres"
        className={`h-12 rounded-full px-3 text-sm ${text.unit === 'ml' ? 'bg-accent/15 text-accent' : 'text-ink-muted ring-1 ring-white/15'}`}
        onClick={() => {
          onText({ ...text, unit: text.unit === 'g' ? 'ml' : 'g' })
        }}
      >
        {text.unit === 'g' ? 'g' : 'ml'}
      </button>
      {onRemove !== undefined && (
        <button type="button" aria-label="Remove portion" className="icon-btn" onClick={onRemove}>
          <DeleteIcon aria-hidden className="size-6" />
        </button>
      )}
    </div>
  )
}
